import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaCog,
  FaSignOutAlt,
  FaUser,
  FaLightbulb,
  FaUsers,
  FaBriefcase,
  FaArrowRight,
  FaHistory,
  FaTrophy,
  FaCogs,
  FaBook,
  FaEnvelope,
  FaHome,
  FaExternalLinkAlt,
  FaClock,
  FaCheckCircle,
  FaUserTie,
  FaChartLine
} from 'react-icons/fa';
import './Admin.css';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    newEnquiries: 0,
    totalEnquiries: 0,
    totalClients: 0,
    totalPortfolio: 0
  });
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        if (currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
          setUser(currentUser);
          fetchStats();
        } else {
          signOut(auth);
          navigate('/admin');
        }
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      // Fetch enquiries stats
      const enquiriesSnap = await getDocs(collection(db, 'contactMessages'));
      const enquiries = enquiriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const newEnquiries = enquiries.filter(e => e.status === 'new' || !e.status).length;

      // Get recent enquiries
      const recentSnap = await getDocs(
        query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc'), limit(5))
      );
      const recent = recentSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Fetch clients count
      const clientsSnap = await getDocs(collection(db, 'clients'));

      // Fetch portfolio count
      const portfolioSnap = await getDocs(collection(db, 'portfolio'));

      setStats({
        newEnquiries,
        totalEnquiries: enquiries.length,
        totalClients: clientsSnap.size,
        totalPortfolio: portfolioSnap.size
      });
      setRecentEnquiries(recent);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="admin-loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const contentCards = [
    {
      icon: <FaLightbulb />,
      iconClass: 'expertise',
      title: 'Our Expertise',
      description: 'Manage expertise items with images and features',
      link: '/admin/expertise'
    },
    {
      icon: <FaCogs />,
      iconClass: 'solutions',
      title: 'Solutions',
      description: 'RPA tools, technologies, and use cases',
      link: '/admin/solutions'
    },
    {
      icon: <FaUsers />,
      iconClass: 'clients',
      title: 'Reference Clients',
      description: 'Client logos, ratings, and testimonials',
      link: '/admin/clients'
    },
    {
      icon: <FaHistory />,
      iconClass: 'timeline',
      title: 'Company Timeline',
      description: 'Milestones and company history',
      link: '/admin/timeline'
    },
    {
      icon: <FaTrophy />,
      iconClass: 'achievements',
      title: 'Achievements',
      description: 'Company stats and accomplishments',
      link: '/admin/achievements'
    },
    {
      icon: <FaUserTie />,
      iconClass: 'team',
      title: 'Team / Founders',
      description: 'Team member profiles and info',
      link: '/admin/team'
    },
    {
      icon: <FaBriefcase />,
      iconClass: 'portfolio',
      title: 'Portfolio',
      description: 'Project showcases and case studies',
      link: '/admin/portfolio'
    },
    {
      icon: <FaBook />,
      iconClass: 'resources',
      title: 'Resources',
      description: 'Guides, whitepapers, and downloads',
      link: '/admin/resources'
    }
  ];

  return (
    <div className="admin-dashboard-new">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">O</span>
            <span className="logo-text">OPTEAM</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-title">Main</span>
            <a href="/admin/dashboard" className="nav-item active">
              <FaChartLine /> Dashboard
            </a>
            <a href="/admin/enquiries" className="nav-item highlight">
              <FaEnvelope />
              Enquiries
              {stats.newEnquiries > 0 && (
                <span className="nav-badge">{stats.newEnquiries}</span>
              )}
            </a>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Content Management</span>
            {contentCards.map((card, index) => (
              <a key={index} href={card.link} className="nav-item">
                {card.icon} {card.title}
              </a>
            ))}
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Quick Links</span>
            <a href="/" target="_blank" rel="noopener noreferrer" className="nav-item">
              <FaHome /> View Website <FaExternalLinkAlt className="external-icon" />
            </a>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              <FaUser />
            </div>
            <div className="user-info">
              <span className="user-name">Admin</span>
              <span className="user-email">{user.email}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="topbar-left">
            <h1>Dashboard</h1>
            <p>Welcome back! Here's what's happening with your website.</p>
          </div>
          <div className="topbar-right">
            <a href="/" target="_blank" rel="noopener noreferrer" className="view-site-btn">
              <FaHome /> View Site
            </a>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card enquiries" onClick={() => navigate('/admin/enquiries')}>
            <div className="stat-icon">
              <FaEnvelope />
            </div>
            <div className="stat-content">
              <span className="stat-number">{stats.newEnquiries}</span>
              <span className="stat-label">New Enquiries</span>
            </div>
            {stats.newEnquiries > 0 && <div className="stat-badge pulse">New</div>}
          </div>

          <div className="stat-card total">
            <div className="stat-icon">
              <FaEnvelope />
            </div>
            <div className="stat-content">
              <span className="stat-number">{stats.totalEnquiries}</span>
              <span className="stat-label">Total Messages</span>
            </div>
          </div>

          <div className="stat-card clients" onClick={() => navigate('/admin/clients')}>
            <div className="stat-icon">
              <FaUsers />
            </div>
            <div className="stat-content">
              <span className="stat-number">{stats.totalClients}</span>
              <span className="stat-label">Clients</span>
            </div>
          </div>

          <div className="stat-card portfolio" onClick={() => navigate('/admin/portfolio')}>
            <div className="stat-icon">
              <FaBriefcase />
            </div>
            <div className="stat-content">
              <span className="stat-number">{stats.totalPortfolio}</span>
              <span className="stat-label">Portfolio Items</span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="dashboard-content">
          {/* Recent Enquiries */}
          <div className="dashboard-section enquiries-section">
            <div className="section-header">
              <h2><FaEnvelope /> Recent Enquiries</h2>
              <button onClick={() => navigate('/admin/enquiries')} className="see-all-btn">
                See All <FaArrowRight />
              </button>
            </div>
            <div className="enquiries-list">
              {recentEnquiries.length === 0 ? (
                <div className="empty-state">
                  <FaEnvelope />
                  <p>No enquiries yet</p>
                </div>
              ) : (
                recentEnquiries.map((enquiry) => (
                  <div
                    key={enquiry.id}
                    className={`enquiry-row ${enquiry.status !== 'done' ? 'unread' : ''}`}
                    onClick={() => navigate('/admin/enquiries')}
                  >
                    <div className="enquiry-status">
                      {enquiry.status === 'done' ? (
                        <FaCheckCircle className="status-done" />
                      ) : (
                        <FaClock className="status-new" />
                      )}
                    </div>
                    <div className="enquiry-info">
                      <span className="enquiry-name">{enquiry.name}</span>
                      <span className="enquiry-email">{enquiry.email}</span>
                    </div>
                    <div className="enquiry-preview">
                      {enquiry.message?.substring(0, 50)}...
                    </div>
                    <div className="enquiry-date">
                      {formatDate(enquiry.createdAt)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="dashboard-section actions-section">
            <div className="section-header">
              <h2><FaCog /> Quick Actions</h2>
            </div>
            <div className="quick-actions-grid">
              {contentCards.slice(0, 6).map((card, index) => (
                <div
                  key={index}
                  className="quick-action-card"
                  onClick={() => navigate(card.link)}
                >
                  <div className={`action-icon ${card.iconClass}`}>
                    {card.icon}
                  </div>
                  <div className="action-info">
                    <h4>{card.title}</h4>
                    <p>{card.description}</p>
                  </div>
                  <FaArrowRight className="action-arrow" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
